import { subject } from "@casl/ability";
import { QueryStatus } from "@reduxjs/toolkit/dist/query";
import axios, { AxiosRequestConfig } from "axios";
import isNil from "lodash/isNil";

import getErrorDetails from "api/helpers/getErrorDetails";
import {
  addPaginationAndSort,
  ORDER_ASC,
  ORDER_DESC,
} from "api/helpers/pagination";
import paramsSerializer from "api/helpers/paramsSerializer";
import { portalApi, TAGS } from "api/rtkApi";
import { DEFAULT_PAGINATION } from "constants/defaultPagination";
import {
  APPLICATION_ENV,
  ENV_PRODUCTION,
  ENV_SANDBOX,
} from "constants/environment";
import { SUBJECT_CUSTOMER } from "models/role/subject";
import { transformFormat } from "utils";
import {
  PaginationParams,
  preparePaginationParams,
} from "utils/preparePaginationParams";
import { isUUID, UUID } from "utils/uuid";

import {
  Customer,
  CustomerDiscriminator,
  CustomerNames,
  INCORPORATION_DATE_FORMAT,
} from "../models/Customer";
import { mapHierarchyArrayToObject } from "../utils/mapHierarchyArrayToObject";

import type { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import type { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import type { CountryCode } from "models/CountryCode";
import type { RTKMeta } from "models/RTKMeta";
import type { ObjectWithId } from "utils/ObjectWithId";
import type { OrderableColumns } from "../constants/Customers";
import type CustomerAddress from "../models/CustomerAddress";
import type { CustomerCountries } from "../models/CustomerCountries";
import type { BaseCustomerCredentials } from "../models/CustomerCredentials";
import type { CustomerFiltersType } from "../models/CustomerFilter";
import type { CustomerPaymentPreferences } from "../models/CustomerPaymentPreferences";
import type { CustomerProductFlags } from "../models/CustomerProductFlags";
import type { ProductFlagsType } from "../models/ProductFlags";
import type { SettlementPreferences } from "../models/SettlementPreferences";

const CUSTOMER_NAMES_LIMIT = 1000;
const CUSTOMER_NAMES_PAGE = 1;

type CustomerQuery = {
  id: UUID;
};

type PutUpdateCustomerStatusQuery = ObjectWithId & {
  customer: Pick<Customer, "active" | "terminateAt">;
};

type NamesResponse = {
  customerNames: CustomerNames;
};
type NamesParameters = {
  customerIds?: UUID | UUID[];
};

export interface CustomerCredentialsFromApi extends BaseCustomerCredentials {
  createdAt: string;
}

export type GetCustomerResponse = {
  id: UUID;
  active: boolean;
  address: CustomerAddress | null;
  aisPreferences: unknown;
  children: GetCustomerResponse[];
  circuitBreakerEnabled: boolean;
  companyRegistrationNumber: string;
  countryCode: CountryCode;
  createdAt: string;
  credentials: CustomerCredentialsFromApi[];
  discr: CustomerDiscriminator;
  documentNumber: number;
  incorporationDate: string;
  legalName: string;
  merchantCategoryCode: string;
  metadata: unknown | null;
  name: string;
  parent: UUID | null;
  parentCustomer: { id: UUID; name: string };
  paymentPreferences: CustomerPaymentPreferences;
  productFlags: CustomerProductFlags;
  settlementPreferences: SettlementPreferences[];
  terminateAt: string;
};

export interface GetCustomerHierarchyResponse {
  id: string;
  parentId: null | string;
  path: GetCustomerHierarchyResponse["id"][];
  name: string;
  legalName: null | string;
  customerType: CustomerDiscriminator;
  childrenCount: number;
  depth: number;
}

export interface GetCustomerHierarchyObject
  extends GetCustomerHierarchyResponse {
  children: GetCustomerHierarchyObject[];
}

export async function getCustomers(
  qsParams: { customerId?: UUID | UUID[]; search?: string },
  options?: AxiosRequestConfig
): Promise<{ list: GetCustomerResponse[] }> {
  const { customerId, search } = qsParams;

  const params: Record<string, any> = {
    ...addPaginationAndSort<Customer>({
      orderBy: "createdAt",
      order: ORDER_DESC,
      ...DEFAULT_PAGINATION,
    }),
  };

  if (!isNil(customerId)) {
    params["id"] = customerId;
  }

  if (search) {
    params.search = search;
  }

  try {
    let totalItems = 0;
    let list: GetCustomerResponse[] = [];
    let page = 1;
    do {
      const response = await axios({
        ...options,
        method: "GET",
        url: "/customers",
        params: {
          ...params,
          page,
        },
        paramsSerializer,
      });
      const { data = [] } = response;

      totalItems = response.headers["total-items"] || 0;
      list = [
        ...list,
        ...data.map((customer: Customer) =>
          subject(SUBJECT_CUSTOMER, customer)
        ),
      ];
      page++;
    } while (list.length < totalItems);

    return {
      list,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getCustomer(
  id: UUID,
  options?: AxiosRequestConfig
): Promise<Customer> {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/customers/${id}`,
    });
    const { data = {} } = response;

    return subject(SUBJECT_CUSTOMER, data);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function postCustomer(
  customer: Customer & { customer: UUID },
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: "/customers",
      data: customer,
    });
    const { data = {} } = response;

    return await getCustomer(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchCustomer(
  id: UUID,
  customer: Customer,
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/customers/${id}`,
      data: customer,
    });
    const { data = {} } = response;

    return await getCustomer(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchCustomerBanksByCountry(
  id: UUID,
  countries: CustomerCountries,
  options?: AxiosRequestConfig
): Promise<CustomerCountries> {
  try {
    const response = await axios.put<CustomerCountries>(
      `/customers/${id}/countries/enable-banks`,
      countries,
      options
    );
    const { data = [] } = response;

    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getCustomerBanksByCountry(
  id: UUID,
  options?: AxiosRequestConfig
): Promise<CustomerCountries> {
  try {
    const response = await axios.get<CustomerCountries>(
      `/customers/${id}/countries`,
      options
    );
    const { data = [] } = response;

    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export type GetCustomersParameters = {
  customerId?: UUID | UUID[];
  search?: string;
  filter?: Record<string, string>;
};

export type GetCustomersResponse = {
  customers: GetCustomerResponse[];
  totalItems?: number;
};

export interface GetCustomersQuery extends PaginationParams<OrderableColumns> {
  customer?: UUID | UUID[];
  filters?: CustomerFiltersType;
}

export type GetCustomersQueryResponse = {
  customers: Customer[];
  totalItems: number;
};

export type GenerateCredentialsQuery = ObjectWithId;

type GenerateCredentialsResponse = {
  username: string;
  password: string;
  customer: UUID;
};

export type RegenerateCredentialsQuery = GenerateCredentialsQuery;
type RegenerateCredentialsResponse = {
  password: string;
};

export type PatchCustomerPaymentPreferencesQuery = {
  paymentPreferences: CustomerPaymentPreferences;
} & ObjectWithId;

export type GetCustomerCountriesQuery = ObjectWithId;
export type GetCustomerCountriesResponse = CountryCode[];

export type PatchCustomerBanksByCountryQuery = ObjectWithId & {
  countries: CountryCode[];
};
export type PatchCustomerBanksByCountryResponse = CountryCode[];

export type PatchCustomerProductFlagsResponse = ObjectWithId;
export type PatchCustomerProductFlagsRequest = ObjectWithId & {
  productFlag: {
    [key in ProductFlagsType]?: boolean;
  };
};

export type PatchCustomerCircuitBreakerProductFlagResponse = ObjectWithId;
export type PatchCustomerCircuitBreakerProductFlagRequest = ObjectWithId & {
  circuitBreakerEnabled: boolean;
};

export interface GetCustomerHierarchyResponse {
  id: UUID;
  parentId: null | string;
  path: GetCustomerHierarchyResponse["id"][];
  name: string;
  legalName: null | string;
  customerType: CustomerDiscriminator;
  childrenCount: number;
  depth: number;
}

interface GetCustomerHierarchyQuery {
  id: UUID;
  page: number;
  search?: string;
}

export const CUSTOMERS_AUTOCOMPLETE_RESULTS_LIMIT = 100;
const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<GetCustomersQueryResponse, GetCustomersQuery>({
      query: ({ customer, filters, ...rest }) => ({
        url: "customers",
        params: {
          id: customer,
          ...preparePaginationParams(rest),
          ...filters,
        },
      }),
      providesTags: [TAGS.CUSTOMERS],
      transformResponse: (customers: Customer[], meta: RTKMeta) => ({
        customers: customers.map(({ incorporationDate, ...customer }) => ({
          ...customer,
          incorporationDate:
            incorporationDate !== null
              ? transformFormat(incorporationDate, INCORPORATION_DATE_FORMAT)
              : incorporationDate,
        })),
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
    }),
    getCustomersForAutocomplete: builder.query<
      GetCustomersResponse,
      GetCustomersParameters
    >({
      query: ({ customerId, search = "", filter = {} }) => ({
        url: "customers",
        method: "GET",
        params: {
          limit: CUSTOMERS_AUTOCOMPLETE_RESULTS_LIMIT,
          "order[name]": ORDER_ASC,
          page: 1,
          id: isUUID(search) ? search : customerId,
          ...(search.length && {
            search: isUUID(search) ? undefined : search,
          }),
          ...filter,
        },
      }),
      transformResponse: (data: GetCustomerResponse[], meta: RTKMeta) => {
        const totalItems = Number(meta.response.headers.get("total-items"));

        return {
          totalItems,
          customers: data,
        };
      },
      providesTags: [{ type: TAGS.CUSTOMER_AUTOCOMPLETE }],
    }),
    getCustomerHierarchy: builder.query<
      GetCustomerHierarchyResponse[],
      GetCustomerHierarchyQuery
    >({
      query: ({ id, ...params }) => ({
        url: `customers/${id}/hierarchy`,
        method: "GET",
        params: {
          ...params,
        },
      }),
    }),
    getCustomerNames: builder.query<NamesResponse, NamesParameters>({
      query: ({ customerIds }) => ({
        url: "customers",
        method: "GET",
        params: {
          limit: CUSTOMER_NAMES_LIMIT,
          page: CUSTOMER_NAMES_PAGE,
          id: customerIds,
        },
      }),
      transformResponse: (data: GetCustomerResponse[]) => {
        const customerNames = data.reduce<CustomerNames>(
          (names, currentCustomer) => {
            if (currentCustomer?.id && !names[currentCustomer.id]) {
              names[currentCustomer.id] = currentCustomer.name;
            }

            return names;
          },
          {}
        );

        return {
          customerNames,
        };
      },
      providesTags: [TAGS.CUSTOMER_NAMES],
    }),
    getCustomer: builder.query<GetCustomerResponse, CustomerQuery>({
      query: ({ id }) => `customers/${id}`,
      providesTags: (_, __, { id }) => [{ type: TAGS.CUSTOMER, id }],
      transformResponse: ({
        incorporationDate,
        ...customer
      }: GetCustomerResponse) =>
        subject(SUBJECT_CUSTOMER, {
          ...customer,
          incorporationDate:
            incorporationDate !== null
              ? transformFormat(incorporationDate, INCORPORATION_DATE_FORMAT)
              : incorporationDate,
        }),
    }),
    getCustomerHierarchyTree: builder.query<
      GetCustomerHierarchyObject,
      CustomerQuery
    >({
      query: ({ id }) => `customers/${id}/hierarchy`,
      providesTags: (_, __, { id }) => [{ type: TAGS.CUSTOMER_HIERARCHY, id }],
      transformResponse: (data: GetCustomerHierarchyResponse[]) =>
        mapHierarchyArrayToObject(data[0], data),
    }),
    updateCustomerStatus: builder.mutation<
      Customer,
      PutUpdateCustomerStatusQuery
    >({
      query: ({ id, customer }) => ({
        url: `/customers/${id}`,
        // Once customer is deactivated we have to reset the termination date
        body: { ...(!customer.active && { terminateAt: null }), ...customer },
        method: "PUT",
      }),

      async onQueryStarted(
        { id, customer: incomingCustomer },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            extendedApi.util.updateQueryData(
              "getCustomer",
              { id },
              (customer) => {
                customer.active = incomingCustomer.active;
                customer.terminateAt = incomingCustomer.terminateAt ?? "";
              }
            )
          );

          dispatch(
            extendedApi.util.invalidateTags([
              {
                type: TAGS.CUSTOMERS,
              },
            ])
          );
        } catch {
          dispatch(
            extendedApi.util.invalidateTags([
              {
                type: TAGS.CUSTOMERS,
                id,
              },
            ])
          );
        }
      },
    }),
    getCustomerCountries: builder.query<
      GetCustomerCountriesResponse,
      GetCustomerCountriesQuery
    >({
      query: ({ id }) => `customers/${id}/countries`,
      providesTags: (result, error, { id }) => [
        { type: TAGS.CUSTOMER_COUNTRIES, id },
      ],
    }),
    generateProdCredentials: builder.mutation<
      GenerateCredentialsResponse,
      GenerateCredentialsQuery
    >({
      extraOptions: {
        env: "prod",
      },
      query: ({ id }) => ({
        url: `/customers/${id}/credentials`,
        method: "POST",
      }),
      async onCacheEntryAdded(
        { id },
        { dispatch, cacheDataLoaded, getCacheEntry }
      ) {
        await cacheDataLoaded;
        const { data, status, isError } = getCacheEntry();

        if (status !== QueryStatus.fulfilled || isError || !data) {
          return;
        }

        const customerToUpdate: CustomerCredentialsFromApi = {
          id: data.customer,
          email: data.username,
          environment: "production",
          active: true,
          createdAt: "TBU",
        };

        dispatch(
          extendedApi.util.updateQueryData(
            "getCustomer",
            { id },
            (cachedCustomer: GetCustomerResponse) => {
              Object.assign(cachedCustomer, {
                credentials: [...cachedCustomer.credentials, customerToUpdate],
              });
            }
          )
        );
      },
    }),
    generateSandboxCredentials: builder.mutation<
      GenerateCredentialsResponse,
      GenerateCredentialsQuery
    >({
      extraOptions: {
        env: "sandbox",
      },
      query: ({ id }) => ({
        url: `/customers/${id}/credentials`,
        method: "POST",
      }),
      async onCacheEntryAdded(
        { id },
        { dispatch, cacheDataLoaded, getCacheEntry }
      ) {
        await cacheDataLoaded;
        const { data, status, isError } = getCacheEntry();

        if (status !== QueryStatus.fulfilled || isError || !data) {
          return;
        }

        const customerToUpdate: CustomerCredentialsFromApi = {
          id: data.customer,
          email: data.username,
          environment: "sandbox",
          active: true,
          createdAt: "TBU",
        };

        dispatch(
          extendedApi.util.updateQueryData(
            "getCustomer",
            { id },
            (cachedCustomer: GetCustomerResponse) => {
              Object.assign(cachedCustomer, {
                credentials: [...cachedCustomer.credentials, customerToUpdate],
              });
            }
          )
        );
      },
    }),
    regenerateProdCredentials: builder.mutation<
      RegenerateCredentialsResponse,
      RegenerateCredentialsQuery
    >({
      extraOptions: {
        env: "prod",
      },
      query: ({ id }) => ({
        url: `/customers/${id}/credentials/regenerate`,
        method: "POST",
      }),
    }),
    regenerateSandboxCredentials: builder.mutation<
      RegenerateCredentialsResponse,
      RegenerateCredentialsQuery
    >({
      extraOptions: {
        env: "sandbox",
      },
      query: ({ id }) => ({
        url: `/customers/${id}/credentials/regenerate`,
        method: "POST",
      }),
    }),
    patchCustomerPaymentPreferences: builder.mutation<
      void,
      PatchCustomerPaymentPreferencesQuery
    >({
      query: ({ id, paymentPreferences }) => ({
        url: `/customers/${id}/preferences/payments`,
        method: "PATCH",
        body: paymentPreferences,
      }),
      async onQueryStarted(
        { id, paymentPreferences },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            extendedApi.util.updateQueryData(
              "getCustomer",
              { id },
              (customer) => {
                customer.paymentPreferences = paymentPreferences;
              }
            )
          );
        } catch {
          dispatch(
            extendedApi.util.invalidateTags([
              {
                type: TAGS.CUSTOMERS,
                id,
              },
            ])
          );
        }
      },
    }),
    patchCustomerBanksByCountry: builder.mutation<
      PatchCustomerBanksByCountryResponse,
      PatchCustomerBanksByCountryQuery
    >({
      query: ({ id, countries }) => ({
        url: `/customers/${id}/countries/enable-banks`,
        method: "PUT",
        body: countries,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: countries } = await queryFulfilled;

          dispatch(
            extendedApi.util.updateQueryData(
              "getCustomerCountries",
              { id },
              () => countries
            )
          );
        } catch {}
      },
    }),
    patchSandboxCustomerProductFlags: buildPatchCustomerProductFlagMutation(
      ENV_SANDBOX,
      builder
    ),
    patchProductionCustomerProductFlags: buildPatchCustomerProductFlagMutation(
      ENV_PRODUCTION,
      builder
    ),
    patchCircuitBreakerProductFlag: builder.mutation<
      PatchCustomerCircuitBreakerProductFlagResponse,
      PatchCustomerCircuitBreakerProductFlagRequest
    >({
      query: ({ id, circuitBreakerEnabled }) => ({
        url: `/customers/${id}`,
        method: "PUT",
        body: { circuitBreakerEnabled },
      }),
      async onQueryStarted(
        { id, circuitBreakerEnabled },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            extendedApi.util.updateQueryData(
              "getCustomer",
              { id },
              (customer) => {
                customer.circuitBreakerEnabled = circuitBreakerEnabled;
              }
            )
          );
        } catch {
          dispatch(
            extendedApi.util.invalidateTags([
              {
                type: TAGS.CUSTOMERS,
                id,
              },
            ])
          );
        }
      },
    }),
  }),
  overrideExisting: false,
});

function buildPatchCustomerProductFlagMutation(
  env: APPLICATION_ENV,
  builder: EndpointBuilder<BaseQueryFn, string, string>
) {
  const fieldName = env === ENV_PRODUCTION ? "production" : "sandbox";

  return builder.mutation<
    PatchCustomerProductFlagsResponse,
    PatchCustomerProductFlagsRequest
  >({
    extraOptions: {
      env,
    },
    query: ({ id, productFlag }) => ({
      url: `/customers/${id}/product-flags`,
      method: "PATCH",
      body: productFlag,
    }),
    async onQueryStarted({ id, productFlag }, { dispatch, queryFulfilled }) {
      try {
        await queryFulfilled;
        dispatch(
          extendedApi.util.updateQueryData(
            "getCustomer",
            { id },
            (customer) => {
              customer.productFlags = {
                ...customer.productFlags,
                [fieldName]: {
                  ...customer.productFlags[fieldName],
                  ...productFlag,
                },
              };
            }
          )
        );
      } catch {
        dispatch(
          extendedApi.util.invalidateTags([
            {
              type: TAGS.CUSTOMERS,
              id,
            },
          ])
        );
      }
    },
  });
}

export const {
  useGetCustomerHierarchyQuery,
  useGenerateProdCredentialsMutation,
  useGenerateSandboxCredentialsMutation,
  useGetCustomerNamesQuery,
  useGetCustomerQuery,
  useGetCustomerHierarchyTreeQuery,
  useGetCustomerCountriesQuery,
  useGetCustomersForAutocompleteQuery,
  useGetCustomersQuery,
  useRegenerateProdCredentialsMutation,
  useRegenerateSandboxCredentialsMutation,
  usePatchCustomerPaymentPreferencesMutation,
  usePatchCustomerBanksByCountryMutation,
  useUpdateCustomerStatusMutation,
  usePatchSandboxCustomerProductFlagsMutation,
  usePatchProductionCustomerProductFlagsMutation,
  usePatchCircuitBreakerProductFlagMutation,
} = extendedApi;

export { extendedApi as customersApi };
